import { useCallback, useRef, useState } from "react";
import { t } from "i18next";
import { fetchVideoLinkPreview } from "@apis/http/api";
import type { ResponseOf } from "@apis/http/typedRoute";
import { ensureHttps } from "@pages/aiStudio/ScriptExport/insight";
import { useDebounce } from "@hooks/utils";

export type ReferenceVideoLinkStatus = "idle" | "loading" | "succeeded" | "failed";

export type VideoLinkPreviewData = ResponseOf<"POST/api/video/link-preview">;

const LINK_ERROR_MESSAGE = () => t("Currently supports links from TikTok, Kalodata, and YouTube Shorts.");

export const useReferenceVideoLink = () => {
  const [input, setInputState] = useState("");
  const [status, setStatus] = useState<ReferenceVideoLinkStatus>("idle");
  const [preview, setPreview] = useState<VideoLinkPreviewData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const requestSeqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    requestSeqRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setInputState("");
    setStatus("idle");
    setPreview(null);
    setErrorMessage(null);
  }, []);

  const parseLink = useCallback(async (rawInput: string) => {
    const trimmed = rawInput.trim();
    if (!trimmed) {
      setStatus("idle");
      setPreview(null);
      setErrorMessage(null);
      return;
    }

    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus("loading");
    setErrorMessage(null);
    setPreview(null);

    try {
      const data = await fetchVideoLinkPreview({ videoLink: ensureHttps(trimmed) }, { signal: controller.signal });
      if (requestSeqRef.current !== seq) return;

      if (!data?.originVideoLink && !data?.coverImageUrl) {
        setStatus("failed");
        setErrorMessage(LINK_ERROR_MESSAGE());
        return;
      }

      setPreview(data);
      setStatus("succeeded");
    } catch {
      if (controller.signal.aborted || requestSeqRef.current !== seq) return;
      setStatus("failed");
      setErrorMessage(LINK_ERROR_MESSAGE());
    }
  }, []);

  const setInput = useCallback((value: string) => {
    setInputState(value);
    setStatus("idle");
    setPreview(null);
    setErrorMessage(null);
  }, []);

  const parseLinkRef = useRef(parseLink);
  parseLinkRef.current = parseLink;

  const debouncedParse = useDebounce((value: string) => {
    if (value.trim()) void parseLinkRef.current(value);
  }, 500);

  return {
    input,
    setInput,
    status,
    preview,
    errorMessage,
    parseLink,
    debouncedParse,
    reset,
  };
};
